import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../app/AppContext';
import { Notice } from '../components/Notice';
import type { CalibrationProfile, EnvironmentSnapshot } from '../core/types';
import {
  calculatePixelsPerCentimeter,
  createEnvironmentSnapshot,
  createId,
  sampleFrameDiagnostics,
  type FrameDiagnostics,
} from '../core';

export function CalibrationPage() {
  const app = useApp();
  const navigate = useNavigate();
  const [cardWidth, setCardWidth] = useState(() => Math.round((app.calibration?.pixelsPerCentimeter ?? 37.8) * 8.56));
  const [distance, setDistance] = useState(app.calibration?.viewingDistanceCm ?? 60);
  const [method, setMethod] = useState<'card' | 'ruler'>(app.calibration?.method ?? 'card');
  const [correction, setCorrection] = useState<CalibrationProfile['correction']>(app.calibration?.correction ?? 'none');
  const [diagnostic, setDiagnostic] = useState<FrameDiagnostics>();
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [operationError, setOperationError] = useState<string>();
  const [safetyAccepted, setSafetyAccepted] = useState(false);
  const [pointerReady, setPointerReady] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  const physicalWidthCm = method === 'card' ? 8.56 : 10;
  const pixelsPerCentimeter = calculatePixelsPerCentimeter(cardWidth, physicalWidthCm);
  const valuesValid = Number.isFinite(distance) && distance >= 40 && distance <= 120 && Number.isFinite(pixelsPerCentimeter) && pixelsPerCentimeter > 0;
  const runDiagnostic = async () => {
    setRunning(true);
    setOperationError(undefined);
    try {
      setDiagnostic(await sampleFrameDiagnostics({ sampleCount: 120 }));
    } catch {
      setDiagnostic(undefined);
      setOperationError('描画診断を実行できませんでした。ブラウザを再読み込みして再試行してください。');
    } finally {
      setRunning(false);
    }
  };

  const save = async () => {
    if (!diagnostic?.stable || !valuesValid || !pointerReady || !safetyAccepted) return;
    setSaving(true);
    setOperationError(undefined);
    try {
      const calibration: CalibrationProfile = {
        id: createId('cal'),
        createdAt: new Date().toISOString(),
        pixelsPerCentimeter,
        viewingDistanceCm: distance,
        method,
        correction,
      };
      const environment: EnvironmentSnapshot = createEnvironmentSnapshot(diagnostic, { calibrationId: calibration.id });
      await app.saveCalibration(calibration, environment);
      navigate('/');
    } catch {
      setOperationError('設定をこのブラウザへ保存できませんでした。空き容量とサイトデータの許可を確認し、もう一度お試しください。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page onboarding-page">
      <header className="onboarding-header">
        <div><div className="eyebrow">トレーニング前の準備</div><h1>画面と見る位置を合わせます</h1><p>正しく表示するための初回設定です。約5分で終わります。</p></div>
        <ol className="step-indicator" aria-label="準備の進み具合">
          {['安全確認', '画面サイズ', '見る位置', '動作確認'].map((label, index) => <li key={label} className={step === index + 1 ? 'current' : step > index + 1 ? 'complete' : ''}><span>{index + 1}</span>{label}</li>)}
        </ol>
      </header>

      {step === 1 && <section className="panel onboarding-panel">
        <span className="step-count">1 / 4</span>
        <h2>体調を確認してください</h2>
        <p className="lead-copy">画面上で動くものを目で追います。体調がよいときに、座った状態で行ってください。</p>
        <div className="safety-list">
          <div><span aria-hidden="true">✓</span><p><strong>眼鏡やコンタクトは普段どおり</strong>トレーニング中も毎回同じ条件にします。</p></div>
          <div><span aria-hidden="true">!</span><p><strong>違和感が出たらすぐに中止</strong>頭痛、めまい、吐き気、かすみ、ものが二重に見える場合は続けません。</p></div>
          <div><span aria-hidden="true">i</span><p><strong>医療検査ではありません</strong>診断、脳震盪の判定、競技復帰の判断には使えません。</p></div>
        </div>
        <label className="confirmation safety-confirmation"><input type="checkbox" checked={safetyAccepted} onChange={(event) => setSafetyAccepted(event.target.checked)} /><span><strong>上の内容を確認しました</strong></span></label>
        <div className="onboarding-actions"><button className="button" type="button" disabled={!safetyAccepted} onClick={() => setStep(2)}>次へ</button></div>
      </section>}

      {step === 2 && <section className="panel onboarding-panel wide">
          <span className="step-count">2 / 4</span>
          <h2>画面上の大きさを合わせます</h2>
          <p className="lead-copy">実物のカード、または定規を画面に当てて、表示の横幅を合わせてください。</p>
          <div className="choice-list" role="radiogroup" aria-label="画面サイズを合わせる方法">
            <label className="choice-card"><input type="radio" name="method" checked={method === 'card'} onChange={() => setMethod('card')} /><span><strong>クレジットカードなどを使う</strong><span>一般的なカードの横幅に合わせます</span></span><span>おすすめ</span></label>
            <label className="choice-card"><input type="radio" name="method" checked={method === 'ruler'} onChange={() => setMethod('ruler')} /><span><strong>定規を使う</strong><span>10センチの長さに合わせます</span></span></label>
          </div>
          <div className="calibration-stage" aria-label="実寸調整プレビュー">
            <svg width={Math.min(cardWidth + 8, 570)} height="222" role="img" aria-label={`${method === 'card' ? '標準カード' : '10センチ線'}の幅`}>
              {method === 'card' ? (
                <g>
                  <rect className="calibration-svg-card" x="4" y="9" width={Math.min(cardWidth, 560)} height="204" rx="13" />
                  <rect className="calibration-svg-inset" x="18" y="23" width={Math.max(0, Math.min(cardWidth, 560) - 28)} height="176" rx="8" />
                  <text className="calibration-svg-text" x={Math.min(cardWidth, 560) / 2 + 4} y="116" textAnchor="middle">実物のカードに合わせる</text>
                </g>
              ) : (
                <g><line className="calibration-ruler" x1="4" y1="111" x2={Math.min(cardWidth, 560) + 4} y2="111" /><line className="calibration-ruler" x1="4" y1="91" x2="4" y2="131" /><line className="calibration-ruler" x1={Math.min(cardWidth, 560) + 4} y1="91" x2={Math.min(cardWidth, 560) + 4} y2="131" /><text className="calibration-ruler-text" x={Math.min(cardWidth, 560) / 2 + 4} y="86" textAnchor="middle">10センチ</text></g>
              )}
            </svg>
          </div>
          <div className="field range-field"><label htmlFor="card-width">横幅を調整する</label><input id="card-width" type="range" min="220" max="560" value={cardWidth} onChange={(event) => setCardWidth(Number(event.target.value))} /><span className="field-hint">つまみを左右に動かし、実物とぴったり重なるようにします。</span></div>
          <div className="onboarding-actions"><button className="button secondary" type="button" onClick={() => setStep(1)}>戻る</button><button className="button" type="button" onClick={() => setStep(3)}>合いました</button></div>
      </section>}

      {step === 3 && <section className="panel onboarding-panel">
        <span className="step-count">3 / 4</span>
        <h2>見る位置を決めます</h2>
        <p className="lead-copy">定規やメジャーで、目から画面までの距離を測って入力してください。</p>
        <div className="form-grid calibration-form single-column">
          <div className="field"><label htmlFor="distance">目から画面までの距離</label><div className="input-with-unit"><input id="distance" type="number" min="40" max="120" value={distance} onChange={(event) => setDistance(Number(event.target.value))} /><span>センチ</span></div><span className="field-hint">40〜120センチの範囲で入力します。おすすめは60センチ前後です。</span></div>
          <div className="field"><label htmlFor="correction">今回の状態</label><select id="correction" value={correction} onChange={(event) => setCorrection(event.target.value as CalibrationProfile['correction'])}><option value="none">裸眼</option><option value="glasses">眼鏡を使用</option><option value="contacts">コンタクトレンズを使用</option></select><span className="field-hint">次回以降も同じ状態で行ってください。</span></div>
        </div>
        {!valuesValid && <Notice title="距離を確認してください。" tone="danger">40〜120センチの数値を入力してください。</Notice>}
        <div className="onboarding-actions"><button className="button secondary" type="button" onClick={() => setStep(2)}>戻る</button><button className="button" type="button" disabled={!valuesValid} onClick={() => setStep(4)}>次へ</button></div>
      </section>}

      {step === 4 && <section className="panel onboarding-panel wide">
          <span className="step-count">4 / 4</span>
          <h2>画面とクリック操作を確認します</h2>
          <p className="lead-copy">最後に、画面が滑らかに動くことと、ボタンを問題なく押せることを確認します。</p>
          <div className="final-check-grid">
            <div className="check-block">
              <div className={`check-status ${diagnostic?.stable ? 'done' : ''}`}><span>{diagnostic?.stable ? '✓' : '1'}</span><div><strong>画面の動作確認</strong><small>{diagnostic?.stable ? '問題ありません' : '約2秒かかります'}</small></div></div>
              <button className="button secondary diagnostic-button" type="button" onClick={runDiagnostic} disabled={running}>{running ? '確認しています…' : diagnostic?.stable ? 'もう一度確認' : '画面を確認する'}</button>
            </div>
            <div className="check-block">
              <div className={`check-status ${pointerReady ? 'done' : ''}`}><span>{pointerReady ? '✓' : '2'}</span><div><strong>クリック操作の確認</strong><small>{pointerReady ? '正常に反応しました' : '下の丸いボタンを押します'}</small></div></div>
              <button className={`pointer-check-button ${pointerReady ? 'done' : ''}`} type="button" onClick={() => setPointerReady(true)} aria-label="クリック操作を確認する">
                <span aria-hidden="true">{pointerReady ? '✓' : ''}</span>
                {pointerReady ? '確認できました' : 'ここをクリック'}
              </button>
            </div>
          </div>
          {diagnostic
            ? diagnostic.stable
              ? null
              : <Notice title="画面の動きが安定していません。" tone="danger">ほかのアプリやタブを閉じて、もう一度確認してください。</Notice>
            : null}
          {operationError && <Notice title="処理を完了できませんでした。" tone="danger">{operationError}</Notice>}
          <div className="onboarding-actions"><button className="button secondary" type="button" onClick={() => setStep(3)}>戻る</button><button className="button" type="button" onClick={save} disabled={!diagnostic?.stable || !valuesValid || !pointerReady || !safetyAccepted || saving}>{saving ? '保存しています…' : '準備を完了する'}</button></div>
      </section>}
    </div>
  );
}
